import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Loader2, Building2, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * <InstitutionAutocomplete /> — search-as-you-type institution picker
 * backed by /api/institutions/search (OpenAlex proxy).
 *
 * Props:
 *   value          — { id, name, country, city, type, source } | null
 *   onChange(inst) — called with the selected institution (or null when cleared)
 *   placeholder    — input placeholder
 *   disabled       — disable the field entirely
 *
 * UX rules:
 *   - 2 character minimum before lookups fire
 *   - 220ms debounce
 *   - keyboard navigable dropdown (Up/Down/Enter/Esc)
 *   - clearing the input removes the institution
 */
export default function InstitutionAutocomplete({
  value,
  onChange,
  placeholder = "School, college or uni",
  disabled = false,
}) {
  const [query, setQuery] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const selectedId = value?.id || null;

  // Reset the displayed text whenever a fresh value is pushed in from the parent.
  useEffect(() => {
    setQuery(value?.name || "");
  }, [value]);

  // Click-outside closes the dropdown
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const runSearch = useMemo(
    () => async (q) => {
      setError("");
      setLoading(true);
      try {
        const { data } = await api.get(`/institutions/search`, { params: { q, limit: 8 } });
        setResults(data.results || []);
        if (data.error) setError("Search is busy — try again in a moment.");
      } catch {
        setError("Couldn't search right now.");
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    setActive(-1);
    // Clearing the field clears the selection entirely.
    if (!v.trim()) {
      if (selectedId) onChange(null);
      setResults([]);
      return;
    }
    if (v.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v.trim()), 220);
  };

  const pick = (inst) => {
    onChange(inst);
    setQuery(inst.name);
    setResults([]);
    setOpen(false);
    setActive(-1);
    // Blur so the input's onFocus doesn't immediately re-open the dropdown
    // and so click-outside-listeners that consume focus work as expected.
    inputRef.current?.blur();
  };

  const clear = () => {
    onChange(null);
    setQuery("");
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(results.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && active < results.length) pick(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showEmpty = open && !loading && query.trim().length >= 2 && results.length === 0 && !error;
  const showHint = open && query.trim().length > 0 && query.trim().length < 2;

  return (
    <div className="relative" ref={wrapRef} data-testid="institution-autocomplete">
      <div className="flex items-center gap-2 bg-white rounded-2xl border-2 border-pink-100 px-3 py-2 focus-within:border-pink-300">
        <Building2 className="w-4 h-4 text-pink-500 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={onInput}
          onFocus={() => {
            // Only re-open if there's still a search-in-progress query, not when
            // the input already matches the picked value (avoids the dropdown
            // popping right back open after selection).
            if (query.trim().length >= 2 && query.trim() !== (value?.name || "")) {
              setOpen(true);
            }
          }}
          onKeyDown={onKey}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="institution-listbox"
          className="flex-1 bg-transparent outline-none font-body text-sm text-slate-900 placeholder-slate-400"
          data-testid="institution-input"
        />
        {loading ? (
          <Loader2 className="w-4 h-4 text-slate-400 animate-spin shrink-0" />
        ) : value || query ? (
          <button
            type="button"
            onClick={clear}
            className="text-slate-400 hover:text-pink-600"
            aria-label="Clear institution"
            data-testid="institution-clear-btn"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <Search className="w-4 h-4 text-slate-300 shrink-0" />
        )}
      </div>

      {open && (loading || results.length > 0 || showEmpty || showHint || error) ? (
        <div
          id="institution-listbox"
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-2xl border-2 border-slate-100 shadow-xl overflow-hidden max-h-72 overflow-y-auto"
          data-testid="institution-dropdown"
        >
          {showHint ? (
            <div className="px-4 py-3 text-xs font-body text-slate-500">Keep typing — at least 2 letters.</div>
          ) : null}
          {loading ? (
            <div className="px-4 py-3 text-xs font-body text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching institutions…
            </div>
          ) : null}
          {results.map((inst, i) => {
            const isSelected = selectedId === inst.id;
            const isActive = i === active;
            return (
              <button
                key={inst.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(inst)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-slate-50 last:border-b-0 ${isActive ? "bg-pink-50" : "bg-white"} hover:bg-pink-50`}
                data-testid={`institution-option-${inst.id}`}
              >
                <Building2 className="w-4 h-4 text-pink-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900 text-sm truncate">{inst.name}</div>
                  <div className="text-[11px] font-body text-slate-500 truncate">
                    {[inst.city, inst.country].filter(Boolean).join(", ")}
                  </div>
                </div>
                {isSelected ? <Check className="w-4 h-4 text-emerald-500 shrink-0" /> : null}
              </button>
            );
          })}
          {showEmpty ? (
            <div className="px-4 py-3 text-xs font-body text-slate-500">
              No matches. Try the full name (e.g. &quot;University of Sussex&quot;).
            </div>
          ) : null}
          {error ? (
            <div className="px-4 py-3 text-xs font-body text-rose-600">{error}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
